import {
    TimeEntry,
    SlotType,
    FreeSlot,
    ReservedSlot,
    VacationSlot,
    Slot,
} from '@/components/calendar/types';
import { CalendarConsultation, CalendarConsultationRequest } from '@/types/models/calendar';

import { Consultation, Doctor, Patient, Clinic } from '@/types/common';
import { ISOCalendarDate, ISODateRange, ISOTime } from '@/utils/date/types';
import { SelectItem } from '@/components/select/types';

export interface BackendSlotsRequest {
    doctorId: number,
    dateTimeStart: ISOCalendarDate,
    dateTimeEnd: ISOCalendarDate,
}

export interface SlotsRequest {
    doctorId: number,
    range: ISODateRange,
}

interface BackendBaseSlot {
    startTime: string,
    endTime: string,
    // datetime: string, // 01.06.2021 16:00 (convert to 2021-06-01 16:00)
}

interface BackendSlotEmpty extends BackendBaseSlot {
    isReserved: false,
}

export interface BackendSlotReserved extends BackendBaseSlot {
    isReserved: true,
    startTime: string,
    endTime: string,
    consultation: Consultation,
    doctor: Doctor,
    patient: Patient,
    clinic?: Clinic,
}

export interface BackendSlotCustom {
    chair?: string,
    fid?: string,
    type: string,
    user?: string,
    created_at: string, // "2021-10-16T00:00:00+03:00"
    updated_at: string, // "2021-10-16T00:00:00+03:00"
}

type BackendSlot = BackendSlotEmpty | BackendSlotReserved;

export interface BackendWorkingDay {
    startWorkDay: ISOTime,
    endWorkday: ISOTime,
    freeSlotsCount: number,
    slotsDate: string,
    clinicId: number,
    clinicTitle: string,
    slots: Record<ISOTime, BackendSlot> | [],
    custom: BackendSlotCustom,
}

interface BackendVacationDay {
    slot_date: string, // "2021-10-16T00:00:00+03:00"
    full_interval: TimeEntry,
    type: 'vacation',
    vacation_id: number,
}

// На одну дату теперь может приходить несколько рабочих дней (по клиникам)
type BackendDay = BackendWorkingDay[] | BackendVacationDay;

export type BackendSlots = Record<ISOCalendarDate, BackendDay>
export type SlotClinic = SelectItem<number>
export type SlotsClinic = Record<ISOCalendarDate, SlotClinic[]>
export type SlotsCustom = Record<ISOCalendarDate, BackendSlotCustom>
export type Slots = Record<ISOCalendarDate, Slot[]>

// transforms

const createTimeEntry = (slot: BackendBaseSlot, date: ISOCalendarDate): TimeEntry => {
    const start = `${date} ${slot.startTime}`;
    const end = `${date} ${slot.endTime}`;

    return {
        start,
        end,
    };
}

const transformFreeSlot = (slot: BackendSlotEmpty, date: ISOCalendarDate, clinic?: SlotClinic): FreeSlot => {
    const entry = createTimeEntry(slot, date);

    const base: FreeSlot = {
        ...entry,
        type: SlotType.Free,
        name: slot.startTime,
    }

    // для свободных слотов пробрасываем клинику, если она известна на уровне дня
    if (clinic) {
        (base as any).clinic = clinic;
    }

    return base;
}

export const transformReservedSlot = (slot: BackendSlotReserved, date: ISOCalendarDate, clinicFromDay?: SlotClinic): ReservedSlot => {
    const title = slot.consultation.title;
    const entry = createTimeEntry(slot, date);

    const base: ReservedSlot = {
        ...entry,
        type: SlotType.Reserved,
        name: `${slot.startTime} ${title}`,
        consultation: slot.consultation,
        doctor: slot.doctor,
        patient: slot.patient,
    }

    // если в слоте нет клиники, пробрасываем её с уровня дня
    if (slot.clinic) {
        base.clinic = slot.clinic;
    } else if (clinicFromDay) {
        base.clinic = {
            id: clinicFromDay.id,
            title: clinicFromDay.value,
        } as any;
    }

    return base;
}

const transformVacationSlot = (backendDay: BackendVacationDay, date: ISOCalendarDate): VacationSlot => {
    const entry = createTimeEntry({
        startTime: '00:00',
        endTime: '24:00',
    }, date);

    return {
        type: SlotType.Vacation,
        start: entry.start,
        end: entry.end,
        dates: backendDay.full_interval,
        name: 'Отпуск',
        vacationId: backendDay.vacation_id,
    }
}

export const transformSlots = (calendar: BackendSlots): [Slots, SlotsClinic, SlotsCustom] => {
    const slotsClinic: [string, SlotClinic[]][] = [];
    const slotsCustom: [string, BackendSlotCustom][] = [];
    const slots = Object.entries(calendar).map(([date, dayOrDays]) => {
        // отпуск по дню
        if (!Array.isArray(dayOrDays) && 'type' in dayOrDays) {
            const slot = transformVacationSlot(dayOrDays, date);

            return [
                date,
                [slot],
            ];
        }

        const workingDays: BackendWorkingDay[] = Array.isArray(dayOrDays) ? dayOrDays : [dayOrDays as BackendWorkingDay];

        // агрегируем слоты всех клиник в один список
        const allSlots: Slot[] = [];
        const clinicsForDay: SlotClinic[] = [];

        workingDays.forEach((day) => {
            if (Array.isArray(day.slots)) {
                return;
            }

            const clinic: SlotClinic | null = day.clinicId
                ? { id: day.clinicId, value: day.clinicTitle }
                : null;

            if (clinic && !clinicsForDay.find(c => c.id === clinic.id)) {
                clinicsForDay.push(clinic);
            }

            const backendSlots = Object.values(day.slots);
            const daySlots = backendSlots.map((slot) => {
                if (slot.isReserved) {
                    return transformReservedSlot(slot as BackendSlotReserved, date, clinic || undefined);
                }

                return transformFreeSlot(slot as BackendSlotEmpty, date, clinic || undefined);
            });

            allSlots.push(...daySlots);

            if (day.custom) {
                // сохраняем первый custom по дню
                if (!slotsCustom.find(([d]) => d === date)) {
                    slotsCustom.push([date, day.custom]);
                }
            }
        });

        if (clinicsForDay.length > 0) {
            slotsClinic.push([date, clinicsForDay]);
        }

        return [date, allSlots];
    });

    return [Object.fromEntries(slots), Object.fromEntries(slotsClinic), Object.fromEntries(slotsCustom)];
}

export const transformCalendarConsultation = (doctorId: number, consultation: CalendarConsultation): CalendarConsultationRequest => {
    const data: CalendarConsultationRequest = {
        doctorId,
        startDateTime: consultation.start,
        duration: consultation.duration,
        patientId: consultation.patient.id,
        medicalServiceTypeId: consultation.medicalService.id,
        specializationId: consultation.specialization.id,
    }

    if (consultation.clinic?.id) {
        data.clinicId = consultation.clinic.id;
    }

    return data;
}
